/**
 * CombatMinionDom — Incremental DOM updates for the combat minion boards.
 *
 * The combat board is updated in place instead of being rebuilt: new minions
 * get new elements, existing minions get their stats/classes patched, and
 * removed minions lose their elements.
 *
 * This module is deliberately decoupled from main.ts so the update rules can
 * be unit-tested. In particular it defends against the historical bug where
 * element tracking (the elementMap) was cleared without removing the DOM
 * elements — without the "adopt existing element" check below, the next
 * render would append a second full set of minions on top of the old ones
 * (boards visibly multiplying after each combat).
 */

import type { CombatMinionRenderData } from '../screens/CombatBoardView';

/** Factory that creates a fresh DOM element for a combat minion. */
export type CombatMinionElementFactory = (minion: CombatMinionRenderData) => HTMLElement;

/**
 * Incrementally sync a combat side's minion elements with the current render
 * data.
 *
 * @param container       The side's minion container element.
 * @param minions         Current minions, left-to-right order.
 * @param elementMap      InstanceId → DOM element tracking for this side.
 *                        Mutated in place (removed minions dropped, new added).
 * @param createMinionElement Factory for new elements (may also be used to
 *                            refresh adopted stale elements in place).
 */
export function updateCombatMinionElements(
  container: HTMLElement,
  minions: CombatMinionRenderData[],
  elementMap: Map<string, HTMLElement>,
  createMinionElement: CombatMinionElementFactory,
): void {
  // Build a set of current minion IDs
  const currentIds = new Set(minions.map((m) => m.instanceId));

  // Remove elements for minions no longer present
  for (const [id, el] of elementMap) {
    if (!currentIds.has(id)) {
      el.remove();
      elementMap.delete(id);
    }
  }

  // Update or create elements for each minion (the minions array is sorted
  // by battlefield position, left to right).
  for (let i = 0; i < minions.length; i++) {
    const minion = minions[i];
    let el = elementMap.get(minion.instanceId);
    if (!el) {
      // Defence in depth: adopt a stale element left in the container for the
      // same minion (e.g., from a previous combat whose tracking was cleared
      // without removing the DOM) instead of appending a duplicate.
      const existing = container.querySelector(
        `.combat-minion[data-instance-id="${minion.instanceId}"]`
      ) as HTMLElement | null;
      if (existing) {
        // Fully refresh in place — stats/level/abilities may have changed
        // between combats.
        const fresh = createMinionElement(minion);
        existing.replaceWith(fresh);
        el = fresh;
      } else {
        el = createMinionElement(minion);
        // Insert at the minion's battlefield position, NOT at the end of the
        // row: a mid-combat spawn (e.g., a Necromancy Undead replacing the
        // leftmost minion) must land in the replaced slot. A plain
        // appendChild would put it at the right end of the flex row even
        // though the render data has it sorted by position.
        const laterIds = new Set<string>();
        for (let j = i + 1; j < minions.length; j++) {
          laterIds.add(minions[j].instanceId);
        }
        let refNode: Node | null = null;
        for (const child of Array.from(container.children)) {
          const childId = (child as HTMLElement).dataset?.instanceId;
          if (childId !== undefined && laterIds.has(childId)) {
            refNode = child;
            break;
          }
        }
        if (refNode) {
          container.insertBefore(el, refNode);
        } else {
          container.appendChild(el);
        }
      }
      elementMap.set(minion.instanceId, el);
      // The frozen overlay applies from the moment the element exists (the
      // freeze event streams AFTER the minion is on the board, so this
      // mostly hits re-created elements — fresh elements start unfrozen).
      el.classList.toggle('frozen', !!minion.frozen);
    } else {
      // Update existing element in-place (only stats that change during combat)
      const hpSpan = el.querySelector('.minion-hp') as HTMLElement;
      const atkSpan = el.querySelector('.minion-atk') as HTMLElement;
      if (hpSpan) hpSpan.textContent = `❤${minion.currentHp}`;
      if (atkSpan) atkSpan.textContent = `⚔${minion.attack}`;

      // Update HP damage class
      if (hpSpan) {
        hpSpan.classList.toggle('hp-damaged', minion.currentHp < minion.startingHp);
      }

      // Update alive/dead class
      el.classList.toggle('dead', !minion.isAlive);

      // Update the frozen (blue/white ice) overlay class: on while the
      // server has the minion frozen (freeze event), off on unfreeze
      // (freezeSkip) or death.
      el.classList.toggle('frozen', !!minion.frozen);
    }
  }
}

/**
 * Removes every tracked element from its container and clears the tracking
 * map. Call when the combat area is hidden or the match screen is left, so
 * the next combat starts with a clean board.
 */
export function clearCombatMinionElements(
  container: HTMLElement,
  elementMap: Map<string, HTMLElement>,
): void {
  elementMap.forEach((el) => el.remove());
  elementMap.clear();
}
